import { isObject } from '@vueuse/core'
import { array, mixed, object, string } from 'yup'
import type { Activity, ActivityType } from '@/prisma/client'
import { slugify } from '@/composables/useUtils'

export const subdomainRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
export const reservedSubdomains = ['www', 'demo', 'admin', 'api', 'mail', 'ftp', 'blog', 'support', 'help', 'docs', 'status', 'test', 'dev', 'staging', 'local', 'app', 'account', 'auth', 'billing', 'cdn', 'chat', 'community', 'contact', 'dashboard', 'database', 'download', 'email', 'events', 'forum', 'game', 'host', 'info', 'jobs', 'learn', 'login', 'logout', 'media', 'mobile', 'news', 'oauth', 'payment', 'portal', 'privacy', 'register', 'root', 'secure', 'server', 'service', 'shop', 'signup', 'store', 'team', 'terms', 'user', 'video', 'web', 'wiki', 'your', 'quickbooking', 'public']
export const ibanRegex = /^[A-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/
export const vatRegex = /^[0-9]{11}$/
export const urlRegex = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+)(\.[a-zA-Z]{2,}){1,2}(:[0-9]{1,5})?(\/.*)?$/

const isAdmin = computed(() => {
  const { user } = useUserSession()
  return user.value?.role === 'ADMIN'
})

export function activitySchema(fields: string[], t: Function) {
  const baseSchema = {
    name: string().required(t('errors.required')).min(3, t('errors.min', { min: 3 })),
    legalName: string().required(t('errors.required')),
    // residenza privata, b&b, dogsitter, babysitter, influencer
    vatNumber: string().when('activityType', {
      is: (type: any) => !type || type?.vatFree,
      then: schema => schema.nullable(),
      otherwise: schema => schema.required(t('errors.required')).matches(vatRegex, t('errors.vatNumber')),
    }),
    iban: string().matches(ibanRegex, t('errors.iban')).required(t('errors.required')),
    email: string().email(t('errors.email')).required(t('errors.required')),
    phone: string().required(t('errors.required')),
    description: string().nullable(),
    subdomain: string().required(t('errors.required'))
      .matches(subdomainRegex, t('errors.subdomain')).min(3, t('errors.min', { min: 3 })).max(30, t('errors.max', { max: 30 }))
      .test('is-not-reserved', t('errors.reservedSubdomain'), value => isAdmin.value || !reservedSubdomains.includes(value)),
    pageConfig: mixed().nullable(),
    website: string().matches(urlRegex, t('errors.url')).nullable(),
    activityType: object().test('is-valid-type', t('errors.required'), (value) => {
      return value.id !== undefined
    }),
    logo: string().nullable(),
    images: array().of(string()).nullable(),
    city: object().required(t('errors.required')),
    province: string().required(t('errors.required')),
    zipCode: string().required(t('errors.required')),
    address: string().required(t('errors.required')),
    legalCity: object().required(t('errors.required')),
    legalProvince: string().required(t('errors.required')),
    legalZipCode: string().required(t('errors.required')),
    legalAddress: string().required(t('errors.required')),
  }
  const partialSchema = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const generateSubdomain = (name: string): string => {
  return slugify(name)
}

export const useActivityStore = defineStore('activity', () => {
  const userActivities = ref<Activity[] | null>(null)
  const types = ref<ActivityType[] | null>(null)
  const typesList = ref<object[] | undefined>()
  const isFetchingUserActivities = ref(false)
  const cacheTtl = 5 * 60 * 1000 // 5 minutes in milliseconds
  const lastFetchTime = ref<number | null>(null)
  const normalizeData = (activity: any) => {
    const data: any = { ...activity }
    if (activity.city) data.city = activity.city.denominazione_ita
    if (activity.legalCity) data.legalCity = activity.legalCity.denominazione_ita
    if (activity.activityType) {
      data.activityType = {
        connect: { id: activity.activityType.id },
      }
    }
    if (activity.stripeAccount) {
      data.stripeAccount = {
        connect: { id: activity.stripeAccount.id },
      }
    } else {
      delete data.stripeAccount
      delete data.stripeAccountId
    }
    if (activity.pageConfig) {
      if (isObject(activity.pageConfig)) {
        data.pageConfig = activity.pageConfig
      } else if (typeof activity.pageConfig === 'string') {
        try {
          data.pageConfig = JSON.parse(activity.pageConfig)
        } catch (e) {
          console.error('Error parsing pageConfig:', e)
          data.pageConfig = null
        }
      } else {
        data.pageConfig = null
      }
    }
    return data
  }
  async function fetch(subdomain: string) {
    // Usata per prendere tutti i dati di una attività, inclusi tipo, categorie e servizi
    const data = ref<Activity[] | null>(null)
    const status = ref<'pending' | 'success' | 'error'>('pending')
    const error = ref<any | null>(null)
    try {
      isFetchingUserActivities.value = true
      const result = await $fetch<Activity[] | null>(`/api/activity/${subdomain}`)
      if (result) {
        const index = userActivities.value?.findIndex(a => a.id === result.id)
        if (index !== -1) {
          userActivities.value?.splice(index, 1, result)
        }
      }
      data.value = result
      status.value = 'success'
    } catch (e: any) {
      status.value = 'error'
      error.value = e
    } finally {
      isFetchingUserActivities.value = false
    }
    return { data, status, error }
  }
  async function fetchUserActivities(force: boolean = false): Promise<{ data: Ref<Activity[] | null>, status: Ref<'pending' | 'success' | 'error'>, error: Ref<any | null> }> {
    // Usata per prendere dati generali di tutte
    const now = Date.now()
    const isCacheValid = lastFetchTime.value && now - lastFetchTime.value < cacheTtl
    if (isFetchingUserActivities.value || (!force && isCacheValid && userActivities.value !== null)) {
      return {
        data: userActivities,
        status: ref('success'),
        error: ref(null),
      }
    }
    isFetchingUserActivities.value = true
    const data = ref<Activity[] | null>(null)
    const status = ref<'pending' | 'success' | 'error'>('pending')
    const error = ref<any | null>(null)
    try {
      const result = await $fetch<Activity[] | null>('/api/activity/user')
      if (result) {
        userActivities.value = result
        lastFetchTime.value = now
      }
      data.value = result
      status.value = 'success'
    } catch (e) {
      status.value = 'error'
      error.value = e
    } finally {
      isFetchingUserActivities.value = false
    }
    return { data, status, error }
  }
  async function upsert(activity: Partial<Activity>) {
    try {
      const result = await $fetch<Activity | null>('/api/activity', {
        method: 'POST',
        body: normalizeData(activity),
      })
      if (result) {
        const index = userActivities.value?.findIndex(a => a.id === activity.id)
        if (index !== -1) {
          userActivities.value?.splice(index, 1, result)
        } else {
          userActivities.value = [...(userActivities.value || []), result]
        }
      }
      return { data: result, status: 'success', error: null }
    } catch (e) {
      return { data: null, status: 'error', error: e.data }
    }
  }
  async function createMany(activities: Activity[]) {
    try {
      const results = []
      const errors = []
      for (const activity of activities) {
        const data = normalizeData(activity)
        try {
          const result = await $fetch<Activity | null>('/api/activity', {
            method: 'POST',
            body: data,
          })
          results.push(result)
        } catch (e: any) {
          errors.push({ activity: activity.name, error: e })
        }
      }
      if (errors.length > 0) {
        return { data: results, status: 'error', error: errors }
      } else {
        return { data: results, status: 'success', error: null }
      }
    } catch (e) {
      return { data: null, status: 'error', error: e }
    }
  }
  async function fetchTypes() {
    try {
      types.value = await $fetch<ActivityType[] | null>('/api/activity/type')
      const parents = types.value?.filter(type => type.parentId === null) || []
      const children = types.value?.filter(type => type.parentId !== null) || []
      parents.sort((a, b) => a.order - b.order)
      // Build Nested Structure
      const nestedTypes = parents.reduce((acc, parent) => {
        acc[parent.id] = { type: 'label', children: [], ...parent }
        return acc
      }, {} as { [key: number]: { label: string, value: number, type: string, children: any[], order: number, vatFree: boolean } })
      // Add Children to Parents
      children.forEach((child) => {
        const parent = nestedTypes[child.parentId!]
        if (parent) {
          parent.children.push(child)
          child.parentName = parent.name
        }
      })
      // Sort Children within each Parent
      Object.values(nestedTypes).forEach((parent) => {
        parent.children.sort((a, b) => {
          const typeA = types.value?.find(t => t.id === a.value)
          const typeB = types.value?.find(t => t.id === b.value)
          return (typeA?.order ?? 0) - (typeB?.order ?? 0)
        })
      })
      // Flatten the Structure for USelect
      const sortedNestedTypes = Object.values(nestedTypes).sort((a, b) => a.order - b.order)
      typesList.value = sortedNestedTypes.flatMap((item, index, array) => {
        const result = [item]
        if (item.children.length > 0) {
          result.push(...item.children)
        }
        // Add separator only if there are more items after this one
        if (index < array.length - 1 && item.children.length > 0) {
          result.push({ type: 'separator' })
        }
        return result
      })
      // console.log(types.value, typesList. value)
      return { data: types.value, status: 'success', error: null }
    } catch (e) {
      return { data: types.value, status: 'error', error: e }
    }
  }
  async function isSubdomainFree(subdomain: string, activityId?: number, onboardingId?: string) {
    try {
      if (!isAdmin.value && reservedSubdomains.includes(subdomain)) return false
      if (localStorage.getItem('onboarding-activity')) {
        const onboardingActivities = JSON.parse(localStorage.getItem('onboarding-activity') || '[]')
        if (onboardingActivities.find(activity => activity.subdomain === subdomain && activity.onboardingId !== onboardingId)) return false
      }
      if (userActivities.value?.find(activity => activity.subdomain === subdomain && activity.id !== activityId)) return false
      const result = await $fetch<ActivityType[] | null>('/api/activity/subdomain-check', { query: { subdomain, activityId } })
      return parseInt(result) === 0
    } catch (error) {
      return { data: null, status: 'error', error }
    }
  }
  return {
    fetch,
    fetchUserActivities,
    createMany,
    upsert,
    fetchTypes,
    isSubdomainFree,
    userActivities,
    types,
    typesList,
  }
})
